import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { messagingApi, socialApi } from '../../support/supportApi';
import MessagingPeoplePanel from './MessagingPeoplePanel';
import MessagingThreadPanel from './MessagingThreadPanel';
import MessagingEmptyPanel from './MessagingEmptyPanel';
import NewMessagePicker from './NewMessagePicker';
import {
  normalizeMessagingUser,
  preserveMessagingProfileMode,
  conversationMatchesPeer,
  messagingPeerKey,
} from './messagingUserUtils';
import { dispatchMessagingUnreadChanged, MESSAGING_UNREAD_CHANGED_EVENT } from './messagingEvents';
import { PROFILE_UPDATED_EVENT } from '../profilePictureUrl';
import '../../../layout/ReusableComponent/Text-editor/TextEditor.css';
import './Messaging.css';

function buildThreadSearchParams(user) {
  const normalized = normalizeMessagingUser(user);
  if (!normalized?.userId) return {};
  const params = { userId: normalized.userId };
  if (normalized.profileMode === 'social') params.profileMode = 'social';
  return params;
}

/**
 * Full messages at /sm/all-messages (social shell) or /messages (chat-only app), and embedded in mobile overlay.
 */
export default function MessagingPage({
  embedded = false,
  mobileFullscreen = false,
  initialUserId = '',
  viewerUserId = '',
  viewerUser = null,
  onViewPeerProfile = null,
  standalone = false,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdParam = initialUserId || searchParams.get('userId') || '';
  const profileModeParam =
    searchParams.get('profileMode') === 'social' ? 'social' : 'personal';
  const composeParam = searchParams.get('compose') === '1';

  const [activeTab, setActiveTab] = useState('all');
  const [conversations, setConversations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [activeConversation, setActiveConversation] = useState(null);
  const [pendingOtherUser, setPendingOtherUser] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [selectingPeerKey, setSelectingPeerKey] = useState('');
  const urlSyncedPeerKeyRef = useRef('');
  const openRequestRef = useRef(0);
  const switchedToUnreadTabRef = useRef(false);

  const clearConversationUnread = useCallback((conversationId) => {
    const convId = String(conversationId || '').trim();
    if (!convId) return;
    setConversations((prev) =>
      prev.map((c) => (c.conversationId === convId ? { ...c, unreadCount: 0 } : c))
    );
    setActiveConversation((prev) =>
      prev?.conversationId === convId ? { ...prev, unreadCount: 0 } : prev
    );
    dispatchMessagingUnreadChanged();
  }, []);

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setListLoading(true);
    try {
      const data = await messagingApi.conversations({ limit: 80 });
      if (data?.success) setConversations(data.conversations || []);
    } catch {
      if (!silent) setConversations([]);
    } finally {
      if (!silent) setListLoading(false);
    }
  }, []);

  const refreshConversations = useCallback(async () => {
    try {
      const data = await messagingApi.conversations({ limit: 80 });
      if (!data?.success) return;
      const nextConversations = data.conversations || [];
      setConversations(nextConversations);
      setActiveConversation((prev) => {
        if (!prev?.conversationId) return prev;
        const updated = nextConversations.find((c) => c.conversationId === prev.conversationId);
        return updated || prev;
      });
    } catch {
      /* ignore silent refresh errors */
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const onProfileUpdated = () => loadConversations({ silent: true });
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, [loadConversations]);

  useEffect(() => {
    const onFocus = () => loadConversations({ silent: true });
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [loadConversations]);

  useEffect(() => {
    if (listLoading || switchedToUnreadTabRef.current) return undefined;
    if (conversations.some((c) => (c.unreadCount || 0) > 0)) {
      setActiveTab('all');
      switchedToUnreadTabRef.current = true;
    }
    return undefined;
  }, [listLoading, conversations]);

  useEffect(() => {
    if (composeParam && !embedded) {
      setComposeOpen(true);
    }
  }, [composeParam, embedded]);

  const threadOtherUser = activeConversation?.otherUser || pendingOtherUser;
  const selectedPeerKey = selectingPeerKey || messagingPeerKey(threadOtherUser);

  const applyThreadSelection = useCallback((peerKey, { existing, normalized, syncUrl }) => {
    setComposeOpen(false);
    if (existing) {
      setActiveConversation(existing);
      setPendingOtherUser(null);
    } else {
      setActiveConversation(null);
      setPendingOtherUser(normalized);
    }
    setSelectingPeerKey(peerKey);
    if (syncUrl && !embedded) {
      urlSyncedPeerKeyRef.current = peerKey;
      setSearchParams(buildThreadSearchParams(normalized));
    }
  }, [embedded, setSearchParams]);

  const openUserThread = useCallback(
    async (user, { syncUrl = true } = {}) => {
      const normalized = normalizeMessagingUser(user);
      if (!normalized?.userId) return;

      const peerKey = messagingPeerKey(normalized);
      const settledPeerKey = messagingPeerKey(
        activeConversation?.otherUser || pendingOtherUser
      );
      const existing = conversations.find((c) => conversationMatchesPeer(c, normalized));

      if (
        settledPeerKey === peerKey &&
        !selectingPeerKey &&
        (activeConversation || pendingOtherUser)
      ) {
        if (syncUrl && !embedded) setSearchParams(buildThreadSearchParams(normalized));
        return;
      }

      const requestId = ++openRequestRef.current;
      applyThreadSelection(peerKey, { existing, normalized, syncUrl });

      const needsProfileFetch =
        !normalized.hasProfilePicture ||
        !normalized.displayName ||
        normalized.displayName === 'User';

      let enriched = normalized;
      if (needsProfileFetch) {
        const profileMode = normalized.profileMode || 'personal';
        try {
          const profileData = await socialApi.publicProfile({
            userId: normalized.userId,
            profileMode,
          });
          if (openRequestRef.current !== requestId) return;
          const profile = profileData?.profile;
          if (profile) {
            enriched = preserveMessagingProfileMode(
              {
                userId: normalized.userId,
                profileMode,
                name: profile.name || [profile.first_name, profile.last_name].filter(Boolean).join(' '),
                profile_picture_url: profile.profile_picture_url,
                handle: profile.handle || profile.personal_handle || profile.social_handle,
                personal_handle: profile.personal_handle || profile.handle,
                social_handle: profile.social_handle || profile.handle,
              },
              normalized
            );
          }
        } catch {
          /* use list data */
        }
      }

      if (openRequestRef.current !== requestId) return;

      if (existing) {
        setActiveConversation({ ...existing, otherUser: enriched });
        setPendingOtherUser(null);
        if (openRequestRef.current === requestId) setSelectingPeerKey('');
        return;
      }

      setPendingOtherUser(enriched);
      try {
        const statusData = await messagingApi.status({
          userId: enriched.userId,
          recipientProfileMode: enriched.profileMode,
        });
        if (openRequestRef.current !== requestId) return;
        const status = statusData?.status;
        if (
          statusData?.success &&
          status?.conversationId &&
          (status?.hasConversation || status?.messageCount > 0)
        ) {
          setActiveConversation({
            conversationId: status.conversationId,
            otherUser: enriched,
          });
          setPendingOtherUser(null);
        }
      } catch {
        /* new conversation */
      } finally {
        if (openRequestRef.current === requestId) setSelectingPeerKey('');
      }
    },
    [
      activeConversation,
      pendingOtherUser,
      selectingPeerKey,
      conversations,
      embedded,
      setSearchParams,
      applyThreadSelection,
    ]
  );

  useEffect(() => {
    if (embedded || listLoading) return undefined;
    if (!userIdParam) {
      urlSyncedPeerKeyRef.current = '';
      return undefined;
    }
    const peerKey = `${userIdParam}:${profileModeParam}`;
    if (urlSyncedPeerKeyRef.current === peerKey) return undefined;
    urlSyncedPeerKeyRef.current = peerKey;
    openUserThread({ userId: userIdParam, profileMode: profileModeParam }, { syncUrl: false });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- URL-driven initial selection only
  }, [userIdParam, profileModeParam, embedded, listLoading]);

  const openConversation = (conv) => {
    const other = normalizeMessagingUser(conv?.otherUser);
    if (!other?.userId) return;
    openRequestRef.current += 1;
    setComposeOpen(false);
    setActiveConversation(conv);
    setPendingOtherUser(null);
    setSelectingPeerKey('');
    if (!embedded) {
      urlSyncedPeerKeyRef.current = messagingPeerKey(other);
      setSearchParams(buildThreadSearchParams(other));
    }
  };

  const closeThread = () => {
    openRequestRef.current += 1;
    setActiveConversation(null);
    setPendingOtherUser(null);
    setSelectingPeerKey('');
    setComposeOpen(false);
    urlSyncedPeerKeyRef.current = '';
    if (!embedded) setSearchParams({});
  };

  const showThread = Boolean(
    selectingPeerKey || activeConversation || pendingOtherUser
  );
  const threadConversationId = activeConversation?.conversationId || null;
  const threadOpening = Boolean(selectingPeerKey);

  useEffect(() => {
    if (!showThread) return undefined;
    const onUnreadChanged = () => loadConversations({ silent: true });
    window.addEventListener(MESSAGING_UNREAD_CHANGED_EVENT, onUnreadChanged);
    return () => {
      window.removeEventListener(MESSAGING_UNREAD_CHANGED_EVENT, onUnreadChanged);
    };
  }, [loadConversations, showThread]);

  const threadOtherUserRef = useRef(null);
  threadOtherUserRef.current = threadOtherUser;

  const handleConversationCreated = useCallback((conversationId) => {
    setActiveConversation((prev) =>
      prev?.conversationId
        ? prev
        : {
            conversationId,
            otherUser: threadOtherUserRef.current,
          }
    );
    refreshConversations();
  }, [refreshConversations]);

  const handleStartConversation = () => {
    openRequestRef.current += 1;
    setActiveConversation(null);
    setPendingOtherUser(null);
    setSelectingPeerKey('');
    setComposeOpen(true);
    setActiveTab('following');
    urlSyncedPeerKeyRef.current = '';
    if (!embedded) setSearchParams({ compose: '1' });
  };

  return (
    <div
      className={`messaging-page${embedded ? ' messaging-page--embedded' : ''}${
        standalone ? ' messaging-page--standalone' : ''
      }${mobileFullscreen ? ' messaging-page--mobile-fullscreen' : ''}`}
    >
      <div
        className={`messaging-page__layout${showThread ? ' has-thread' : ''}${
          composeOpen ? ' has-compose' : ''
        }`}
      >
        <MessagingPeoplePanel
          activeTab={activeTab}
          onTabChange={setActiveTab}
          conversations={conversations}
          conversationsLoading={listLoading}
          selectedPeerKey={selectedPeerKey}
          selectingPeerKey={selectingPeerKey}
          viewerUserId={viewerUserId}
          onSelectConversation={openConversation}
          onSelectUser={openUserThread}
          onStartConversation={handleStartConversation}
        />

        <main className="messaging-page__main">
          {composeOpen ? (
            <NewMessagePicker
              embedded={embedded}
              viewerUserId={viewerUserId}
              onSelectUser={openUserThread}
              onClose={() => {
                setComposeOpen(false);
                if (!embedded) {
                  setSearchParams(
                    threadOtherUser ? buildThreadSearchParams(threadOtherUser) : {}
                  );
                }
              }}
            />
          ) : showThread ? (
            <MessagingThreadPanel
              key={selectedPeerKey || 'thread'}
              conversationId={threadConversationId}
              otherUser={threadOtherUser}
              viewerUserId={viewerUserId}
              viewerUser={viewerUser}
              opening={threadOpening}
              pinnedLayout={mobileFullscreen}
              onBack={closeThread}
              onViewPeerProfile={onViewPeerProfile}
              standalone={standalone}
              onConversationCreated={handleConversationCreated}
              onConversationRead={clearConversationUnread}
              onConversationUpdated={refreshConversations}
            />
          ) : (
            <MessagingEmptyPanel onStartConversation={handleStartConversation} />
          )}
        </main>
      </div>
    </div>
  );
}
